// The v1 `uses` names and the one rule for narrowing a project's declared
// grant to them - and nothing else. A deliberately dependency-free leaf:
// `capabilities.ts` (which imports `@hollow/script` to build real bindings)
// re-exports everything here, but always-loaded Studio chrome
// (`editor-panels.tsx`'s Game Setup checklist and console) imports from
// *this* module directly so that narrowing a Set of names never pulls the
// interpreter into the main app bundle - the same boundary
// `hollowScriptRuntime.ts`'s dynamic import protects.

/**
 * The three capabilities spec.md §6 fixes the shape of (`print`, `clock`,
 * `random`), always granted: they touch nothing in a project (no scene, no
 * audio, no logic state), so there is nothing for a per-project grant to
 * protect, and a language whose only way to write text is a capability
 * must have that capability available everywhere a creator can write a
 * script - the same reasoning HollowScript's own CLI applies (ADR-0011).
 */
export const STANDARD_CAPABILITY_NAMES = ['print', 'clock', 'random'] as const;

/** The full v1 capability surface, by `uses` name: `print` first (the one
 * every script reaches for), then the engine-bound capabilities a project
 * grants, then the remaining standard two. */
export const STUDIO_CAPABILITY_NAMES = ['print', 'entities', 'variables', 'audio', 'logic', 'input', 'clock', 'random'] as const;
export type StudioCapabilityName = (typeof STUDIO_CAPABILITY_NAMES)[number];

/** The engine-bound capabilities a project's Game Setup grant can narrow -
 * `STUDIO_CAPABILITY_NAMES` minus the always-granted standard three. */
export const PROJECT_GRANTABLE_CAPABILITY_NAMES: readonly StudioCapabilityName[] = STUDIO_CAPABILITY_NAMES.filter(
  (name) => !(STANDARD_CAPABILITY_NAMES as readonly string[]).includes(name),
);

export function isStudioCapabilityName(name: string): name is StudioCapabilityName {
  return (STUDIO_CAPABILITY_NAMES as readonly string[]).includes(name);
}

/**
 * Narrows a project's declared `scriptCapabilities` grant (plain strings,
 * `GameSetupDoc.scriptCapabilities`) to the real v1 names: `undefined`
 * grants every v1 capability (tier 1's default posture); otherwise the
 * declared engine-bound subset, always unioned with the standard three
 * (`print`/`clock`/`random` are never project-gated). Anything invalid or
 * stale is dropped rather than passed through. The single narrowing rule
 * every surface reads - the Preview console, the Code workspace, the
 * published play surface and the editor's own diagnostics - so none of
 * them can drift on what a project's grant means.
 */
export function narrowGrantedCapabilities(allowed: readonly string[] | undefined): ReadonlySet<StudioCapabilityName> | undefined {
  if (allowed === undefined) return undefined;
  return new Set<StudioCapabilityName>([
    ...STANDARD_CAPABILITY_NAMES,
    ...PROJECT_GRANTABLE_CAPABILITY_NAMES.filter((name) => allowed.includes(name)),
  ]);
}
